import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverProps } from '@anytag/shared-atoms';
import { DEFAULT_DATE_FNS_FORMAT } from '@anytag/shared-constants';
import { getDefaultDate } from '../day-picker/helpers';
import { MonthPickerDropdown, MonthPickerDropdownProps, MonthPickerInput, MonthPickerInputProps } from './shared';

export interface MonthPickerSingleProps
  extends Omit<MonthPickerInputProps, 'value' | 'onClick' | 'onClear'>,
    Pick<MonthPickerDropdownProps, 'disabledMonths'> {
  value?: string;
  saveOnBlur?: boolean;
  onChange: (date: string) => void;
  targetProps?: PopoverProps['targetProps'];
}
export const MonthPickerSingle = ({
  value,
  onChange,
  disabled,
  hasError,
  className,
  placeholder,
  disabledMonths,
  clearable = true,
  saveOnBlur = true,
  targetProps
}: MonthPickerSingleProps) => {
  const { t } = useTranslation();
  const [date, setDate] = useState(getDefaultDate(value));

  useEffect(() => {
    setDate(getDefaultDate(value));
  }, [value]);

  const handleSubmit = () => {
    onChange(date ? format(date, DEFAULT_DATE_FNS_FORMAT) : '');
  };

  const resetDate = () => {
    setDate(getDefaultDate(value));
  };

  const handleClickAway = () => {
    if (saveOnBlur) {
      handleSubmit();
    } else if (date) {
      resetDate();
    }
  };

  return (
    <div className={className}>
      <Popover
        id="month-selector"
        align="end"
        withinPortal={false}
        onClose={handleClickAway}
        targetProps={targetProps}
        target={({ isOpen, closePopover, togglePopover }) => (
          <MonthPickerInput
            value={date}
            hasError={hasError}
            disabled={disabled}
            clearable={clearable}
            placeholder={placeholder || t<string>('Select Month')}
            onClick={togglePopover}
            onClear={() => {
              onChange('');
              setDate(undefined);

              if (saveOnBlur && isOpen) {
                closePopover();
              }
            }}
          />
        )}
        withBorder
      >
        {({ closePopover }) => (
          <MonthPickerDropdown
            value={date}
            disabledMonths={disabledMonths}
            onCancel={() => {
              closePopover();
              resetDate();
            }}
            onChange={({ month, year }) => {
              setDate(new Date(year, month, 1));
            }}
            onSubmit={() => {
              closePopover();
              handleSubmit();
            }}
          />
        )}
      </Popover>
    </div>
  );
};
